/**
 * i18n очереди модерации компаний-партнёров (DEV-795, эпик DEV-788).
 *
 * Подмешивается в основные локали через deep-merge в ./index.ts (как agentsAdmin).
 * Блок companyVerifications.* + пункт common.navigation.companyVerifications.
 * Без литеральных фигурных скобок и служебных символов vue-i18n.
 */

type LocaleOverride = Record<string, unknown>

export const companyVerificationsMessages: Record<'ru' | 'en' | 'vn', LocaleOverride> = {
  ru: {
    common: { navigation: { companyVerifications: 'Модерация компаний' } },
    companyVerifications: {
      title: 'Модерация компаний',
      subtitle: 'Приём компаний-партнёров в справочник по заявкам агентов',
      tabs: {
        onReview: 'На приёме',
        noResult: 'Нет результата',
        mine: 'Мои',
        all: 'Все',
      },
      kpi: {
        onReview: 'На приёме',
        noResult: 'Нет результата ИИ',
        acceptedMonth: 'Принято за месяц',
        slaOverdue: 'Просрочено SLA',
      },
      filters: {
        search: 'Поиск',
        searchPlaceholder: 'Название компании…',
        status: 'Статус',
        confidence: 'Уверенность',
        responsible: 'Ответственный',
        anyConfidence: 'Любая',
        reset: 'Сбросить',
      },
      columns: {
        company: 'Компания',
        agent: 'Агент',
        agentChoice: 'Выбор агента',
        confidence: 'Уверен.',
        status: 'Статус',
        responsible: 'Ответственный',
        createdAt: 'Создано',
      },
      status: {
        pending_ai: 'Ждёт ИИ',
        ai_candidates: 'Есть кандидаты',
        ai_no_result: 'Нет результата ИИ',
        agent_selected: 'Агент выбрал',
        manager_review: 'На приёме',
        confirmed: 'Принято',
        expired: 'Просрочено',
        cancelled: 'Отменено',
      },
      noChoice: 'Агент не выбрал',
      overdueMark: 'SLA',
      accept: {
        title: 'Приём компании',
        typedName: 'Ввод агента',
        agent: 'Агент',
        candidates: 'Кандидаты',
        agentChoice: 'выбор агента',
        recommended: 'рекомендовано ИИ',
        source: 'Источник',
        noCandidates:
          'ИИ не нашёл кандидатов — примите как частного агента или создайте компанию вручную.',
        overrideReasonLabel: 'Причина исправления',
        overrideReasonPlaceholder: 'Почему выбран другой кандидат или отклонён выбор агента…',
        btnAccept: 'Принять компанию',
        btnPrivate: 'Частный агент',
        btnCorrect: 'Исправить',
        needReason: 'Укажите причину исправления',
        loadError: 'Не удалось загрузить заявку',
      },
      toast: {
        accepted: 'Компания принята в справочник',
        privateSet: 'Агент отмечен как частный',
        corrected: 'Выбор исправлен',
        error: 'Не удалось выполнить действие',
      },
      empty: {
        title: 'Очередь пуста',
        description: 'Заявки на приём компаний появятся здесь.',
        withFilters: 'По выбранным фильтрам ничего не найдено.',
      },
    },
  },
  en: {
    common: { navigation: { companyVerifications: 'Company moderation' } },
    companyVerifications: {
      title: 'Company moderation',
      subtitle: 'Admit partner companies to the directory from agent requests',
      tabs: {
        onReview: 'In review',
        noResult: 'No result',
        mine: 'Mine',
        all: 'All',
      },
      kpi: {
        onReview: 'In review',
        noResult: 'No AI result',
        acceptedMonth: 'Accepted this month',
        slaOverdue: 'SLA overdue',
      },
      filters: {
        search: 'Search',
        searchPlaceholder: 'Company name…',
        status: 'Status',
        confidence: 'Confidence',
        responsible: 'Responsible',
        anyConfidence: 'Any',
        reset: 'Reset',
      },
      columns: {
        company: 'Company',
        agent: 'Agent',
        agentChoice: 'Agent choice',
        confidence: 'Conf.',
        status: 'Status',
        responsible: 'Responsible',
        createdAt: 'Created',
      },
      status: {
        pending_ai: 'Awaiting AI',
        ai_candidates: 'Has candidates',
        ai_no_result: 'No AI result',
        agent_selected: 'Agent selected',
        manager_review: 'In review',
        confirmed: 'Accepted',
        expired: 'Expired',
        cancelled: 'Cancelled',
      },
      noChoice: 'Agent did not choose',
      overdueMark: 'SLA',
      accept: {
        title: 'Admit company',
        typedName: 'Agent input',
        agent: 'Agent',
        candidates: 'Candidates',
        agentChoice: 'agent choice',
        recommended: 'AI recommended',
        source: 'Source',
        noCandidates:
          'AI found no candidates — accept as a private agent or create a company manually.',
        overrideReasonLabel: 'Override reason',
        overrideReasonPlaceholder: 'Why another candidate was chosen or the agent choice rejected…',
        btnAccept: 'Accept company',
        btnPrivate: 'Private agent',
        btnCorrect: 'Correct',
        needReason: 'Provide an override reason',
        loadError: 'Failed to load the request',
      },
      toast: {
        accepted: 'Company admitted to the directory',
        privateSet: 'Agent marked as private',
        corrected: 'Choice corrected',
        error: 'Action failed',
      },
      empty: {
        title: 'Queue is empty',
        description: 'Company admission requests will appear here.',
        withFilters: 'Nothing matches the selected filters.',
      },
    },
  },
  vn: {
    common: { navigation: { companyVerifications: 'Kiểm duyệt công ty' } },
    companyVerifications: {
      title: 'Kiểm duyệt công ty',
      subtitle: 'Tiếp nhận công ty đối tác vào danh mục theo yêu cầu của đại lý',
      tabs: {
        onReview: 'Đang xét',
        noResult: 'Không có kết quả',
        mine: 'Của tôi',
        all: 'Tất cả',
      },
      kpi: {
        onReview: 'Đang xét',
        noResult: 'AI không có kết quả',
        acceptedMonth: 'Đã nhận trong tháng',
        slaOverdue: 'Quá hạn SLA',
      },
      filters: {
        search: 'Tìm kiếm',
        searchPlaceholder: 'Tên công ty…',
        status: 'Trạng thái',
        confidence: 'Độ tin cậy',
        responsible: 'Phụ trách',
        anyConfidence: 'Bất kỳ',
        reset: 'Đặt lại',
      },
      columns: {
        company: 'Công ty',
        agent: 'Đại lý',
        agentChoice: 'Lựa chọn đại lý',
        confidence: 'Tin cậy',
        status: 'Trạng thái',
        responsible: 'Phụ trách',
        createdAt: 'Tạo lúc',
      },
      status: {
        pending_ai: 'Chờ AI',
        ai_candidates: 'Có ứng viên',
        ai_no_result: 'AI không có kết quả',
        agent_selected: 'Đại lý đã chọn',
        manager_review: 'Đang xét',
        confirmed: 'Đã nhận',
        expired: 'Hết hạn',
        cancelled: 'Đã hủy',
      },
      noChoice: 'Đại lý chưa chọn',
      overdueMark: 'SLA',
      accept: {
        title: 'Tiếp nhận công ty',
        typedName: 'Đại lý nhập',
        agent: 'Đại lý',
        candidates: 'Ứng viên',
        agentChoice: 'đại lý chọn',
        recommended: 'AI đề xuất',
        source: 'Nguồn',
        noCandidates:
          'AI không tìm thấy ứng viên — nhận làm đại lý tư nhân hoặc tạo công ty thủ công.',
        overrideReasonLabel: 'Lý do chỉnh sửa',
        overrideReasonPlaceholder: 'Vì sao chọn ứng viên khác hoặc từ chối lựa chọn của đại lý…',
        btnAccept: 'Tiếp nhận công ty',
        btnPrivate: 'Đại lý tư nhân',
        btnCorrect: 'Chỉnh sửa',
        needReason: 'Nhập lý do chỉnh sửa',
        loadError: 'Không tải được yêu cầu',
      },
      toast: {
        accepted: 'Đã tiếp nhận công ty vào danh mục',
        privateSet: 'Đã đánh dấu đại lý tư nhân',
        corrected: 'Đã chỉnh sửa lựa chọn',
        error: 'Thao tác thất bại',
      },
      empty: {
        title: 'Hàng đợi trống',
        description: 'Các yêu cầu tiếp nhận công ty sẽ xuất hiện ở đây.',
        withFilters: 'Không có kết quả phù hợp với bộ lọc.',
      },
    },
  },
}
